import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ShieldCheck, Trash2, Paperclip, FileDown, Loader2 } from "lucide-react";

const CHUNK = 2 * 1024 * 1024;
// Recherche insensible aux accents et à la casse (« ecolabel » trouve « Écolabel européen »)
const norm = (v) => (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
export async function uploadProofFile(proofId, file, onProgress) {
  const total = Math.ceil(file.size / CHUNK);
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  let res;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("index", i); fd.append("total", total);
    fd.append("filename", file.name); fd.append("content_type", file.type);
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
    res = await api.post(`/proofs/${proofId}/file/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  return res.data;
}

function ProofFile({ proof, onDone }) {
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Max 15 Mo"); return; }
    setPct(0);
    try { await uploadProofFile(proof.id, f, setPct); toast.success("Fichier joint"); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur upload"); }
    finally { setPct(null); }
  };
  const open = async () => {
    const r = await api.get(`/proofs/${proof.id}/file`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return (
    <div className="flex items-center gap-2">
      {proof.file_path && <button onClick={open} className="text-xs text-primary inline-flex items-center gap-1 link-underline" data-testid={`proof-file-open-${proof.id}`}><FileDown className="w-3.5 h-3.5" /> {proof.file_name}</button>}
      <label className="text-xs text-muted-foreground hover:text-primary cursor-pointer inline-flex items-center gap-1" data-testid={`proof-file-label-${proof.id}`}>
        {pct !== null ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {pct}%</> : <><Paperclip className="w-3.5 h-3.5" /> {proof.file_path ? "Remplacer" : "Joindre PDF/image"}</>}
        <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid={`proof-file-input-${proof.id}`} />
      </label>
    </div>
  );
}

const TYPES = [
  { v: "certificate", l: "Certificat tiers" },
  { v: "study", l: "Étude / ACV" },
  { v: "invoice", l: "Facture / preuve achat" },
  { v: "supplier", l: "Attestation fournisseur" },
  { v: "audit", l: "Rapport d'audit" },
  { v: "other", l: "Autre" },
];

export default function ProofsPage() {
  const [proofs, setProofs] = useState([]);
  const load = () => api.get("/proofs").then(r => setProofs(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    await api.delete(`/proofs/${id}`); toast.success("Preuve supprimée"); load();
  };

  return (
    <div className="space-y-8" data-testid="proofs-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="eyebrow mb-1">Coffre</p>
          <h1 className="text-3xl sm:text-4xl font-serif tracking-tight">Preuves ({proofs.length})</h1>
        </div>
        <NewProof onCreated={load} />
      </div>
      <div className="hairline">
        {proofs.length === 0 && (
          <p className="p-10 text-center text-sm text-muted-foreground">Aucune preuve. Ajoutez vos certificats, ACV, attestations…</p>
        )}
        {proofs.map((p, i) => {
          const expDate = p.expires_at ? new Date(p.expires_at) : null;
          const daysLeft = expDate ? Math.round((expDate - new Date()) / (1000*60*60*24)) : null;
          const critical = daysLeft !== null && daysLeft <= 30;
          return (
            <div key={p.id} className={`p-5 grid md:grid-cols-[auto_1fr_auto_auto] gap-5 items-center ${i > 0 ? "border-t border-border" : ""}`} data-testid={`proof-${p.id}`}>
              <ShieldCheck className={`w-5 h-5 ${critical ? "text-[hsl(var(--status-red))]" : "text-[hsl(var(--status-green))]"}`} />
              <div>
                <p className="font-serif text-lg">{p.name}</p>
                <p className="font-mono text-[11px] text-muted-foreground mt-1">
                  {p.type} · {p.issuer || "—"} {p.issued_at ? `· émis ${p.issued_at.slice(0,10)}` : ""}
                </p>
                {p.label && (
                  <p className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`proof-label-${p.id}`}>
                    <span className="px-1.5 py-0.5 rounded-lg font-mono text-[10px] tag-green">{p.label}</span>
                    {p.country && <span className="font-mono text-[10px] text-muted-foreground">{p.country}</span>}
                    {p.label_meta?.eligible && (
                      <span className={`px-1.5 py-0.5 rounded-lg font-mono text-[10px] ${p.label_meta.eligible === "oui" ? "tag-green" : p.label_meta.eligible === "sous_reserve" ? "tag-orange" : "tag-red"}`}>
                        {p.label_meta.eligible === "oui" ? "Éligible coffre" : p.label_meta.eligible === "sous_reserve" ? "Sous réserve" : "Non éligible"}
                      </span>
                    )}
                  </p>
                )}
                {p.notes && <p className="text-sm text-muted-foreground mt-2">{p.notes}</p>}
                <div className="mt-2"><ProofFile proof={p} onDone={load} /></div>
              </div>
              <div className="text-right">
                {p.expires_at ? (
                  <>
                    <p className={`font-mono text-xs ${critical ? "text-[hsl(var(--status-red))]" : "text-muted-foreground"}`}>Expire</p>
                    <p className={`font-serif text-lg ${critical ? "text-[hsl(var(--status-red))]" : ""}`}>{p.expires_at.slice(0,10)}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">J{daysLeft >= 0 ? `-${daysLeft}` : `+${-daysLeft}`}</p>
                  </>
                ) : <p className="font-mono text-xs text-muted-foreground">Sans expiration</p>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(p.id)} data-testid={`del-proof-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewProof({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", type: "certificate", issuer: "", issued_at: "", expires_at: "", notes: "", country: "", label: "" });
  const [countries, setCountries] = useState([]);
  const [labels, setLabels] = useState([]);
  const [labelQuery, setLabelQuery] = useState("");
  const requiresExpiry = f.type === "certificate" || f.type === "audit";

  useEffect(() => { if (open && countries.length === 0) api.get("/labels/countries").then(r => setCountries(r.data.countries || [])); }, [open, countries.length]);
  useEffect(() => {
    setLabelQuery("");
    if (!f.country) { setLabels([]); return; }
    api.get("/labels", { params: { country: f.country } }).then(r => setLabels(r.data.labels || []));
  }, [f.country]);

  const submit = async () => {
    if (requiresExpiry && !f.expires_at) { toast.error("Date d'expiration obligatoire pour ce type de preuve"); return; }
    const payload = { ...f };
    if (payload.issued_at) payload.issued_at = new Date(payload.issued_at).toISOString();
    if (payload.expires_at) payload.expires_at = new Date(payload.expires_at).toISOString();
    else delete payload.expires_at;
    if (!payload.issued_at) delete payload.issued_at;
    try {
      await api.post("/proofs", payload);
      toast.success("Preuve ajoutée");
      setOpen(false); setF({ name: "", type: "certificate", issuer: "", issued_at: "", expires_at: "", notes: "", country: "", label: "" });
      onCreated();
    } catch { toast.error("Erreur"); }
  };
  const upd = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const picked = labels.find(l => l.label === f.label);
  const needle = norm(labelQuery.trim());
  const shownLabels = needle
    ? labels.filter(l => norm(l.label).includes(needle) || norm(l.category).includes(needle))
    : labels;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="add-proof-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </DialogTrigger>
      <DialogContent className="rounded-lg max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Nouvelle preuve</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Nom" value={f.name} onChange={upd("name")} testid="proof-name" />
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">Type</Label>
            <Select value={f.type} onValueChange={(v) => setF(x => ({...x, type: v}))}>
              <SelectTrigger className="rounded-lg h-11" data-testid="proof-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">Pays</Label>
              <Select value={f.country} onValueChange={(v) => setF(x => ({ ...x, country: v, label: "" }))}>
                <SelectTrigger className="rounded-lg h-11" data-testid="proof-country"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent className="max-h-72">{countries.map(c => <SelectItem key={c.code} value={c.code}>{c.name_fr} ({c.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">Label officiel</Label>
              <Select value={f.label} onValueChange={(v) => setF(x => ({ ...x, label: v, issuer: labels.find(l => l.label === v)?.issuer || x.issuer }))} disabled={!f.country}>
                <SelectTrigger className="rounded-lg h-11" data-testid="proof-label"><SelectValue placeholder={f.country ? "Choisir un label" : "Pays d'abord"} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <div className="p-2 sticky top-0 bg-popover z-10">
                    <Input value={labelQuery} onChange={(e) => setLabelQuery(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()} placeholder="Rechercher un label…"
                      className="h-9 rounded-lg" data-testid="proof-label-search" />
                  </div>
                  {shownLabels.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">Aucun label</p>}
                  {shownLabels.map(l => <SelectItem key={`${l.country}-${l.label}`} value={l.label}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {picked && (
            <div className="rounded-lg border border-border p-3 space-y-1" data-testid="proof-label-meta">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{picked.label_type} · {picked.category} · {picked.scope}</p>
              {picked.guarantees && <p className="text-xs text-muted-foreground">{picked.guarantees}</p>}
              <p className="text-[11px]">
                <span className={`px-1.5 py-0.5 rounded-lg font-mono text-[10px] ${picked.eligible === "oui" ? "tag-green" : picked.eligible === "sous_reserve" ? "tag-orange" : "tag-red"}`}>
                  Coffre : {picked.eligible === "oui" ? "éligible" : picked.eligible === "sous_reserve" ? "sous réserve" : "non éligible"}
                </span>{" "}
                <span className="font-mono text-[10px] text-muted-foreground">UE 2024/825 : {picked.conformity}</span>
              </p>
              {picked.watchpoints && <p className="text-[11px] text-[hsl(29_87%_40%)]">Vigilance · {picked.watchpoints}</p>}
              {picked.official_url && <a href={picked.official_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary link-underline">Site officiel ↗</a>}
            </div>
          )}
          <Field label="Émetteur" value={f.issuer} onChange={upd("issuer")} testid="proof-issuer" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Émise le" type="date" value={f.issued_at} onChange={upd("issued_at")} testid="proof-issued" />
            <Field label={requiresExpiry ? "Expire le *" : "Expire le"} type="date" value={f.expires_at} onChange={upd("expires_at")} required={requiresExpiry} testid="proof-expires" />
          </div>
          {requiresExpiry && (
            <p className="text-[11px] text-muted-foreground -mt-2">Une alerte e-mail sera envoyée automatiquement 1 mois puis 1 semaine avant l'expiration.</p>
          )}
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">Notes</Label>
            <Textarea value={f.notes} onChange={upd("notes")} className="rounded-lg" data-testid="proof-notes" />
          </div>
          <DialogFooter><Button onClick={submit} disabled={!f.name || (requiresExpiry && !f.expires_at)} className="rounded-lg bg-primary text-primary-foreground" data-testid="proof-submit">Enregistrer</Button></DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">{label}</Label>
      <Input {...rest} className="rounded-lg h-11 border-border" data-testid={testid} />
    </div>
  );
}
